import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  webinarsAPI,
  CreateWebinarRequest,
  UpdateWebinarRequest,
} from '../api';
import { toast } from 'react-toastify';
import { useT } from '../../../i18n';

export const useGetWebinars = (search?: string) => {
  return useQuery({
    queryKey: ['webinars', search],
    queryFn: () => webinarsAPI.getAllWebinars(search),
    select: (data) => data.data,
  });
};

export const useGetWebinarById = (id: string) => {
  return useQuery({
    queryKey: ['webinar', id],
    queryFn: () => webinarsAPI.getWebinarById(id),
    select: (data) => data.data,
    enabled: !!id,
  });
};

export const useCreateWebinar = () => {
  const queryClient = useQueryClient();
  const t = useT();

  return useMutation({
    mutationFn: (data: CreateWebinarRequest) => webinarsAPI.createWebinar(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['webinars'] });
      toast.success(t('webinars.toast.created'));
    },
    onError: (error: any) => {
      const message = t('webinars.toast.createFailed');
      toast.error(message);
    },
  });
};

export const useUpdateWebinar = () => {
  const queryClient = useQueryClient();
  const t = useT();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWebinarRequest }) =>
      webinarsAPI.updateWebinar(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['webinars'] });
      queryClient.invalidateQueries({ queryKey: ['webinar'] });
      toast.success(t('webinars.toast.updated'));
    },
    onError: (error: any) => {
      const message = t('webinars.toast.updateFailed');
      toast.error(message);
    },
  });
};

export const useDeleteWebinar = () => {
  const queryClient = useQueryClient();
  const t = useT();

  return useMutation({
    mutationFn: (id: string) => webinarsAPI.deleteWebinar(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['webinars'] });
      toast.success(t('webinars.toast.deleted'));
    },
    onError: (error: any) => {
      const message = t('webinars.toast.deleteFailed');
      toast.error(message);
    },
  });
};
